import React from "react";
import { ArrowRight, FileCheck2, ShieldAlert, Zap } from "lucide-react";
import { ServiceRequestForm } from "./ServiceRequestForm";

const features = [
  {
    icon: Zap,
    iconWrap: "bg-blue-500/10 border-blue-500/30 text-blue-400",
    title: "Instant P&L Delivery",
    text: "Drop in bank & credit card statements and get a fully reconciled, foot-checked P&L in seconds — every figure computed in exact cents and tied back to source documents.",
  },
  {
    icon: FileCheck2,
    iconWrap: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400",
    title: "Tax-Grade Classification, Included",
    text: "Every P&L line lands on the right schedule automatically — Form 1065, 1120-S, 1120 and Schedule C — so year-end prep is already done when the P&L is.",
  },
  {
    icon: ShieldAlert,
    iconWrap: "bg-purple-500/10 border-purple-500/30 text-purple-400",
    title: "1031 Exchange & CapEx Built In",
    text: "Qualified Intermediary escrow, cash & mortgage boot, basis carryover and MACRS CapEx are tracked automatically — no manual workpapers.",
  },
];

const LandingView = ({ onSwitchView, onAuthed }) => {
  return (
    <div className="flex-1 overflow-y-auto custom-scrollbar" data-testid="view-landing">
      {/* HERO SECTION */}
      <section className="relative py-28 px-6 text-center overflow-hidden noise-overlay">
        <img
          src="https://images.pexels.com/photos/1313534/pexels-photo-1313534.jpeg?auto=compress&cs=tinysrgb&w=1920"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-black/60 pointer-events-none"></div>
        <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0A0A]/50 to-[#0A0A0A] pointer-events-none"></div>

        <div className="max-w-4xl mx-auto relative z-10 space-y-6">
          <h1 className="text-4xl sm:text-6xl font-black text-white tracking-tight leading-tight">
            Upload Statements. Get a{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-indigo-300 to-emerald-300">
              Tax Audit-Ready P&amp;L — Instantly
            </span>
          </h1>

          <p className="text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed font-normal">
            PnLFlow turns raw bank &amp; credit card statements into a reconciled, audit-ready P&amp;L in seconds — with tax-grade classification to Form 1065, 1120-S, 1120 and Schedule C, plus Section 1031 Exchange and MACRS CapEx handled automatically.
          </p>

          <div className="flex items-center justify-center space-x-4 pt-4">
            <button
              data-testid="launch-workspace-btn"
              onClick={() => onSwitchView("workspace")}
              className="bg-white hover:bg-zinc-200 text-black font-bold px-6 py-3 rounded-md text-sm transition-colors flex items-center space-x-2"
            >
              <span>Launch CPA Workspace</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* CUSTOMER LOGOS BAR */}
      <section className="py-8 border-y border-white/10 bg-white/[0.03] px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-center">
          <span className="text-xs font-bold tracking-widest text-zinc-500 uppercase">
            Trusted by Modern CPAs &amp; Accounting Firms
          </span>
        </div>
      </section>

      {/* FEATURE CARDS SECTION */}
      <section className="py-20 px-6 max-w-6xl mx-auto space-y-12">
        <div className="text-center space-y-2">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
            Your P&amp;L, the Moment the Statements Land
          </h2>
          <p className="text-zinc-400 text-xs sm:text-sm">
            Built for high-volume CPA practices, fractional CFOs, and real estate investment groups that can't wait until month-end close.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="glass-card p-6 rounded-md border border-white/10 space-y-3 transition duration-300 hover:border-white/20 hover:shadow-glow-white hover:-translate-y-1">
                <div className={`w-10 h-10 rounded-md border flex items-center justify-center ${f.iconWrap}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-white">{f.title}</h3>
                <p className="text-xs text-zinc-400 leading-relaxed">{f.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* SERVICE REQUEST INTAKE */}
      <section className="py-20 px-6 border-t border-white/10" id="request-service" data-testid="service-request-section">
        <div className="max-w-2xl mx-auto space-y-8">
          <div className="text-center space-y-2">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white">Request a Service</h2>
            <p className="text-zinc-400 text-xs sm:text-sm">
              Tell us what you need — no sign-up required. We'll create your client portal automatically.
            </p>
          </div>
          <ServiceRequestForm onAuthed={onAuthed} />
        </div>
      </section>
    </div>
  );
};

export default LandingView;
